import React, { useEffect, useRef, useState } from "react";
import Moment from "moment";
import { IconChevronDown, IconChevronUp, IconX } from "@tabler/icons";
import { Button } from "./core";
import { DateRangeInput, SelectInput, TextInput, NumberInput, Checkbox, SearchSelect } from "./inputs";

// Declarative filter panel shared by the admin pages.
//
// fields: [{ key, label, type, options?, advanced?, placeholder?, neutral? }]
//   type: text | number | select | multiselect | searchselect | yesno | daterange | numrange
//   daterange -> values[key + "From"], values[key + "To"]  ("YYYY-MM-DD");
//     required: true keeps a range (no clear button, its chip can't be removed)
//   numrange  -> values[key + "Min"],  values[key + "Max"]
//   multiselect -> array of option values
//   searchselect -> a searchable select for long lists (offers, publishers,
//     members): options, or loadOptions(input) => Promise<options> for an async
//     search; isMulti for several. The value is the option value(s), or the
//     option object(s) with valueAs: "option" (keeps labels of async results
//     for the chips; map them to ids when building the API body).
//   neutral: values that count as "no filter" (e.g. a default "auto")
// Fields marked advanced sit behind "More filters". Active filters show as
// removable chips. Values persist per storageKey in localStorage (per-viewer
// convenience only; the page works without it).

export function loadFilters(storageKey, defaults) {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || "null");
    return saved && typeof saved === "object" ? { ...defaults, ...saved } : { ...defaults };
  } catch (e) {
    return { ...defaults };
  }
}

function saveFilters(storageKey, values) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(values));
  } catch (e) {
    // storage unavailable (private window); filters just won't persist
  }
}

// Drops empty values so the API only sees active filters.
export function activeFilters(values) {
  const out = {};
  Object.entries(values || {}).forEach(([k, v]) => {
    if (v === "" || v === null || v === undefined) return;
    if (Array.isArray(v) && !v.length) return;
    out[k] = v;
  });
  return out;
}

function hasValue(f, values) {
  if (f.type === "daterange") return Boolean(values[f.key + "From"] || values[f.key + "To"]);
  if (f.type === "numrange") {
    const lo = values[f.key + "Min"];
    const hi = values[f.key + "Max"];
    return (lo !== undefined && lo !== "") || (hi !== undefined && hi !== "");
  }
  const v = values[f.key];
  if (Array.isArray(v)) return v.length > 0;
  return v !== undefined && v !== null && v !== "" && !(f.neutral || []).includes(String(v));
}

// Human summary of one active filter, for its chip.
function describe(f, values) {
  const optLabel = (v) => {
    if (v && typeof v === "object") return v.label !== undefined ? v.label : String(v.value);
    return (f.options || []).find((o) => String(o.value) === String(v))?.label || v;
  };
  const fmt = (d) => (d ? Moment(d).format("D MMM YYYY") : "…");
  switch (f.type) {
    case "daterange": {
      const a = values[f.key + "From"];
      const b = values[f.key + "To"];
      return a && b && a === b ? fmt(a) : `${fmt(a)} – ${fmt(b)}`;
    }
    case "numrange": {
      const lo = values[f.key + "Min"];
      const hi = values[f.key + "Max"];
      if (lo !== undefined && lo !== "" && hi !== undefined && hi !== "") return `${lo} – ${hi}`;
      return lo !== undefined && lo !== "" ? `≥ ${lo}` : `≤ ${hi}`;
    }
    case "multiselect": {
      const list = values[f.key] || [];
      return list.length > 2 ? `${list.length} selected` : list.map(optLabel).join(", ");
    }
    case "searchselect": {
      const v = values[f.key];
      if (!Array.isArray(v)) return String(optLabel(v));
      return v.length > 2 ? `${v.length} selected` : v.map(optLabel).join(", ");
    }
    case "yesno":
      return String(values[f.key]) === "1" ? f.yesLabel || "Yes" : f.noLabel || "No";
    case "select":
      return optLabel(values[f.key]);
    default:
      return String(values[f.key]);
  }
}

function clearedValues(f, values) {
  const next = { ...values };
  if (f.type === "daterange") {
    next[f.key + "From"] = "";
    next[f.key + "To"] = "";
  } else if (f.type === "numrange") {
    next[f.key + "Min"] = "";
    next[f.key + "Max"] = "";
  } else if (f.type === "multiselect" || (f.type === "searchselect" && f.isMulti)) {
    next[f.key] = [];
  } else {
    next[f.key] = f.neutral && f.neutral.length ? f.neutral[0] : "";
  }
  return next;
}

// Checkbox dropdown for a handful of options (statuses, modes).
function MultiSelect({ id, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const selected = Array.isArray(value) ? value.map(String) : [];
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const label = selected.length
    ? options
        .filter((o) => selected.includes(String(o.value)))
        .map((o) => o.label)
        .join(", ")
    : "All";
  return (
    <div
      ref={ref}
      className="pa-multi"
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          e.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <button id={id} type="button" className="pa-input pa-multi-toggle" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="pa-multi-label">{label}</span>
        <IconChevronDown size={16} stroke={1.8} aria-hidden="true" />
      </button>
      {open && (
        <div className="pa-multi-menu" role="listbox" aria-multiselectable="true">
          {options.map((o) => (
            <Checkbox
              key={o.value}
              className="pa-multi-option"
              checked={selected.includes(String(o.value))}
              onChange={() => toggle(String(o.value))}
              label={o.label}
            />
          ))}
          {selected.length > 0 && (
            <div className="pa-multi-foot">
              <button type="button" className="pa-link" onClick={() => onChange([])}>
                Clear selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const FilterPanel = ({ fields, values, onChange, onApply, onReset, storageKey, loading, title = "Filters" }) => {
  const [showAdvanced, setShowAdvanced] = useState(() => fields.some((f) => f.advanced && hasValue(f, values)));

  const set = (key, value) => onChange({ ...values, [key]: value });

  const apply = (next = values) => {
    if (storageKey) saveFilters(storageKey, next);
    onApply(next);
  };

  const reset = () => {
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch (e) {
        // ignore
      }
    }
    onReset();
  };

  const removeFilter = (f) => {
    const next = clearedValues(f, values);
    onChange(next);
    apply(next);
  };

  const active = fields.filter((f) => hasValue(f, values));
  const advancedCount = fields.filter((f) => f.advanced).length;
  const visible = fields.filter((f) => !f.advanced || showAdvanced);
  const idFor = (f) => `pa-filter-${storageKey || "x"}-${f.key}`;
  const onEnter = (e) => e.key === "Enter" && apply();

  const renderField = (f) => {
    const id = idFor(f);
    switch (f.type) {
      case "select":
        return <SelectInput id={id} value={values[f.key] ?? ""} options={f.options} onValueChange={(v) => set(f.key, v)} />;
      case "multiselect":
        return <MultiSelect id={id} options={f.options} value={values[f.key]} onChange={(v) => set(f.key, v)} />;
      case "searchselect": {
        const raw = values[f.key];
        const asOption = f.valueAs === "option";
        return (
          <SearchSelect
            id={id}
            options={f.options || []}
            loadOptions={f.loadOptions}
            isMulti={Boolean(f.isMulti)}
            isClearable
            placeholder={f.placeholder || "Any"}
            value={f.isMulti ? (Array.isArray(raw) ? raw : []) : raw === undefined || raw === null ? "" : raw}
            onChange={(v, opt) => set(f.key, asOption ? (f.isMulti ? opt || [] : opt || "") : v)}
            {...(f.noOptionsMessage ? { noOptionsMessage: f.noOptionsMessage } : {})}
          />
        );
      }
      case "yesno":
        return (
          <SelectInput
            id={id}
            value={values[f.key] ?? ""}
            onValueChange={(v) => set(f.key, v)}
            options={[
              { value: "", label: "Any" },
              { value: "1", label: f.yesLabel || "Yes" },
              { value: "0", label: f.noLabel || "No" },
            ]}
          />
        );
      case "daterange":
        return (
          <DateRangeInput
            id={id}
            value={{ from: values[f.key + "From"] || "", to: values[f.key + "To"] || "" }}
            placeholder={f.placeholder || (f.required ? "Pick dates" : "Any date")}
            presets={f.presets}
            minDate={f.minDate}
            maxDate={f.maxDate}
            clearable={!f.required}
            onChange={({ from, to }) => {
              // A required range is never emptied (a cleared picker keeps the old range).
              if (f.required && (!from || !to)) return;
              onChange({ ...values, [f.key + "From"]: from, [f.key + "To"]: to });
            }}
          />
        );
      case "numrange":
        return (
          <div className="pa-numrange">
            <NumberInput
              id={id}
              placeholder="Min"
              aria-label={`${f.label} minimum`}
              value={values[f.key + "Min"] ?? ""}
              onValueChange={(v) => set(f.key + "Min", v)}
              onKeyDown={onEnter}
            />
            <NumberInput
              placeholder="Max"
              aria-label={`${f.label} maximum`}
              value={values[f.key + "Max"] ?? ""}
              onValueChange={(v) => set(f.key + "Max", v)}
              onKeyDown={onEnter}
            />
          </div>
        );
      case "number":
        return <NumberInput id={id} placeholder={f.placeholder || ""} value={values[f.key] ?? ""} onValueChange={(v) => set(f.key, v)} onKeyDown={onEnter} />;
      default:
        return <TextInput id={id} placeholder={f.placeholder || ""} value={values[f.key] ?? ""} onValueChange={(v) => set(f.key, v)} onKeyDown={onEnter} />;
    }
  };

  return (
    <section className="pa-card pa-filters" aria-label={title}>
      <div className="pa-filters-head">
        <h3 className="pa-card-title">
          {title}
          {active.length > 0 && <span className="pa-tab-count pa-count-brand">{active.length}</span>}
        </h3>
        {advancedCount > 0 && (
          <button type="button" className="pa-link pa-filters-more" onClick={() => setShowAdvanced(!showAdvanced)} aria-expanded={showAdvanced}>
            {showAdvanced ? "Fewer filters" : `More filters (${advancedCount})`}
            {showAdvanced ? <IconChevronUp size={14} stroke={2} aria-hidden="true" /> : <IconChevronDown size={14} stroke={2} aria-hidden="true" />}
          </button>
        )}
      </div>
      <div className="pa-filter-grid">
        {visible.map((f) => (
          <div key={f.key} className="pa-field">
            <label htmlFor={idFor(f)}>{f.label}</label>
            {renderField(f)}
          </div>
        ))}
      </div>
      <div className="pa-filter-actions">
        <Button variant="primary" loading={loading} onClick={() => apply()}>
          Apply filters
        </Button>
        <Button onClick={reset} disabled={loading}>
          Reset
        </Button>
      </div>
      {active.length > 0 && (
        <div className="pa-active-filters" aria-label="Active filters">
          {active.map((f) => (
            <span key={f.key} className="pa-filter-chip">
              {f.label}: {describe(f, values)}
              {!f.required && (
                <button type="button" aria-label={`Remove ${f.label} filter`} onClick={() => removeFilter(f)}>
                  <IconX size={12} stroke={2.4} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </section>
  );
};

export default FilterPanel;
